import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Stack, Chip, Typography, Paper, Avatar, Grid, CircularProgress } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Formik } from 'formik';
import TextField from '@mui/material/TextField';
import { Send } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { useAndieChat } from '@/providers/andie-chat-provider';
import posthog from 'posthog-js';

const suggestedHandles = ['nike', 'cocacola', 'zara'];

const ChooseDays = ({
    selectedDays,
    onSelectDays,
    disabled = false,
}: {
    selectedDays: number;
    onSelectDays: (days: number) => void;
    disabled?: boolean;
}) => {
    const { t } = useTranslation();
    const container = useRef<HTMLDivElement>(null);

    useEffect(() => {
        container.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [container]);

    return (
        <Stack ref={container} onLoad={(e) => (e.target as HTMLElement).scrollIntoView()}>
            <Typography variant="h5" mb={2}>
                {t('onboarding.get-handle.choose-content-plan-duration')}
            </Typography>
            <Grid container columns={{ xs: 12 }} width={1} spacing={2}>
                {[
                    {
                        days: 7,
                        title: t('onboarding.get-handle.plan-7-days-title'),
                        subtitle: t('onboarding.get-handle.plan-7-days-subtitle'),
                        available: true,
                        onClick: () => {
                            onSelectDays(7);
                        },
                    },
                    {
                        days: 30,
                        title: t('onboarding.get-handle.plan-30-days-title'),
                        subtitle: t('onboarding.get-handle.plan-30-days-subtitle'),
                    },
                    {
                        days: 60,
                        title: t('onboarding.get-handle.plan-60-days-title'),
                        subtitle: t('onboarding.get-handle.plan-60-days-subtitle'),
                    },
                ].map((plan, idx) => (
                    <Grid key={idx} size={{ xs: 12, md: 4 }} alignContent="center">
                        <Paper
                            elevation={1}
                            sx={{
                                border:
                                    plan.days === selectedDays ? '2px solid blue' : '2px solid transparent',
                                cursor: plan?.available && !disabled ? 'pointer' : 'default',
                                opacity: !plan?.available || disabled ? 0.5 : 1,
                                transition: 'border 0.2s, box-shadow 0.2s',
                                p: 3,
                                '&:hover': {
                                    bgcolor: plan?.available && !disabled ? 'primary.blue' : 'inherit',
                                },
                            }}
                            onClick={() => {
                                if (disabled) return;
                                plan.onClick?.();
                            }}
                        >
                            <Stack alignItems="center" spacing={1}>
                                <Typography variant="h5" fontWeight={500}>
                                    {plan.title}
                                </Typography>
                                <Typography variant="body2">{plan.subtitle}</Typography>
                                <Typography
                                    variant="caption"
                                    color={plan.available ? 'green' : 'text.secondary'}
                                >
                                    {t(plan.available ? 'available' : 'premium')}
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Stack>
    );
};

export const GetHandle = () => {
    const { t } = useTranslation();
    const { messages, sendMessage } = useAndieChat();
    const [submittedHandle, setSubmittedHandle] = useState(false);

    const calledPublicProfileWorkflow = messages.some((m) =>
        m.parts.some(
            (p): boolean =>
                (p.type === 'dynamic-tool' || p.type?.startsWith('tool-')) &&
                (('toolName' in p && p.toolName === 'publicProfileWorkflow') ||
                    p.type === 'tool-publicProfileWorkflow'),
        ),
    );
    const submitHandle = (value: string) => {
        if (submittedHandle || calledPublicProfileWorkflow) return;

        const handle = value.trim().replace(/^@/, '').toLowerCase();
        if (!handle) return;

        setSubmittedHandle(true);
        posthog.capture('onboarding.get-handle', {
            handle,
        });

        sendMessage(handle);
    };

    return (
        <Stack gap={2}>
            <Formik
                initialValues={{ handle: '', step: 0, days: 0 }}
                onSubmit={(values, { setSubmitting, setValues }) => {
                    if (values.days === 0 && values.handle) {
                        setValues({ ...values, step: 1 });
                    }

                    if (values.days > 0 && values.handle) {
                        submitHandle(values.handle);
                    }

                    setSubmitting(false);
                }}
            >
                {({ values, handleChange, handleSubmit, isSubmitting, setValues }) => {
                    const handleLocked = submittedHandle || calledPublicProfileWorkflow;

                    return (
                        <Stack>
                            <Typography variant="h5" mb={2}>
                                {t('onboarding.get-handle.select-social-network')}
                            </Typography>
                            <Grid container columns={{ xs: 12 }} width={1} spacing={2}>
                                {[
                                    {
                                        name: 'Instagram',
                                        icon: 'instagram',
                                        available: true,
                                        selected: true,
                                    },
                                    { name: 'TikTok', icon: 'tiktok', available: false },
                                    { name: 'LinkedIn', icon: 'linkedin', available: false },
                                    { name: 'Facebook', icon: 'facebook', available: false },
                                ].map((network, idx) => (
                                    <Grid key={idx} size={{ xs: 12, sm: 6, md: 3 }} alignContent="center">
                                        <Paper
                                            elevation={1}
                                            sx={{
                                                border: network?.selected
                                                    ? '2px solid blue'
                                                    : '2px solid transparent',
                                                cursor: network?.selected ? 'pointer' : 'default',
                                                opacity: network.available ? 1 : 0.6,
                                            }}
                                        >
                                            <Stack direction="column" alignItems="center" p={3}>
                                                <Avatar
                                                    variant="square"
                                                    src={`/social/${network.icon}.svg`}
                                                />
                                                <Typography variant="body2" pt={1}>
                                                    {network.name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color={network.available ? 'green' : 'text.secondary'}
                                                >
                                                    {t(network.available ? 'available' : 'premium')}
                                                </Typography>
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                            <Stack mt={3}>
                                <TextField
                                    name="handle"
                                    type="text"
                                    placeholder={t('onboarding.chat.instagram-own-handle')}
                                    disabled={isSubmitting || handleLocked}
                                    value={values.handle}
                                    onChange={handleChange}
                                    onKeyUp={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (values.handle && !isSubmitting) {
                                                handleSubmit();
                                            }
                                        }
                                    }}
                                    fullWidth
                                    variant="outlined"
                                    label={t('onboarding.chat.instagram-handle-label')}
                                    required
                                    autoFocus
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">@</InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        disabled={
                                                            handleLocked || !values.handle || isSubmitting
                                                        }
                                                        sx={{ color: 'primary.main' }}
                                                        onClick={() => handleSubmit()}
                                                    >
                                                        <Send />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Stack>
                            {!calledPublicProfileWorkflow && values.step === 0 && (
                                <Stack mt={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('onboarding.chat.handle-suggestions')}
                                    </Typography>
                                    <Stack direction="row" spacing={1} mt={1}>
                                        {suggestedHandles.map((handle, idx) => (
                                            <motion.div
                                                key={idx}
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ duration: 0.4, delay: idx * 0.2 }}
                                                style={{ display: 'inline-block' }}
                                            >
                                                <Chip
                                                    label={`@${handle}`}
                                                    disabled={handleLocked}
                                                    onClick={() => {
                                                        if (handleLocked) return;
                                                        setValues({ ...values, handle, step: 1 });
                                                    }}
                                                    sx={{ cursor: 'pointer' }}
                                                />
                                            </motion.div>
                                        ))}
                                    </Stack>
                                </Stack>
                            )}
                            {values.step !== 0 && (
                                <Stack spacing={3} mt={4}>
                                    <ChooseDays
                                        selectedDays={values.days}
                                        disabled={handleLocked}
                                        onSelectDays={(days) => {
                                            setValues({ ...values, days });
                                            submitHandle(values.handle);
                                        }}
                                    />
                                    {!calledPublicProfileWorkflow && values.days !== 0 && (
                                        <CircularProgress />
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    );
                }}
            </Formik>
        </Stack>
    );
};
